import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Zap, Gauge, KeyRound, ExternalLink, Save, ShieldCheck, Rocket, Trash2 } from "lucide-react";

const INPUT_CLS = "w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px] text-[var(--text-primary)] font-mono";

export default function AISettings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ai-settings"],
    queryFn: () => api.get("/settings/ai").then(r => r.data),
  });
  const [key, setKey] = useState("");
  const save = useMutation({
    mutationFn: (body) => api.patch("/settings/ai", body),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({queryKey:["ai-settings"]}); setKey(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const speed = q.data?.default_speed || "balanced";
  const hasKey = !!q.data?.has_gemini_key;

  return (
       <Layout title="AI Settings." subtitle="AI speed profile and Gemini key override">
      <div className="max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* SPEED PROFILE */}
        <div className="border border-[var(--border)] bg-[var(--surface)] p-5 sm:p-6" data-testid="speed-profile-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 border border-[var(--accent)]/40 text-[var(--accent)]"><Gauge className="w-4 h-4"/></div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">// speed profile</div>
              <div className="font-display font-black text-2xl tracking-tight">Default tier.</div>
            </div>
          </div>
          <p className="text-[12px] text-[var(--text-muted)] leading-relaxed mb-4">
            Choose the default AI tier for orchestration &amp; topic generation. Individual campaigns can override this on the Orchestrate page.
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              onClick={() => save.mutate({default_speed:"fast"})}
              disabled={save.isPending || speed === "fast"}
              data-testid="speed-fast-btn"
              className={`p-3 border text-left transition-colors ${speed === "fast" ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-3.5 h-3.5 text-[var(--accent)]"/>
                <span className="font-mono text-[10px] uppercase tracking-widest">Fast</span>
              </div>
              <div className="text-[12px] font-medium">Fast routing</div>
              <div className="text-[10px] font-mono text-[var(--text-muted)] mt-1">Flash / Haiku first</div>
            </button>
            <button
              onClick={() => save.mutate({default_speed:"balanced"})}
              disabled={save.isPending || speed === "balanced"}
              data-testid="speed-balanced-btn"
              className={`p-3 border text-left transition-colors ${speed === "balanced" ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400"/>
                <span className="font-mono text-[10px] uppercase tracking-widest">Balanced</span>
              </div>
              <div className="text-[12px] font-medium">Balanced routing</div>
              <div className="text-[10px] font-mono text-[var(--text-muted)] mt-1">Pro / Sonnet first</div>
            </button>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            current default: <span className="text-[var(--accent)]">{speed}</span>
          </div>
        </div>

        {/* BYO GEMINI KEY */}
        <div className="border border-[var(--border)] bg-[var(--surface)] p-5 sm:p-6" data-testid="byo-key-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 border border-amber-500/40 text-amber-400"><KeyRound className="w-4 h-4"/></div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">// integration</div>
              <div className="font-display font-black text-2xl tracking-tight">Your Gemini key.</div>
            </div>
          </div>
          <p className="text-[12px] text-[var(--text-muted)] leading-relaxed mb-3">
             Connect a personal Gemini API key from Google AI Studio. Gemini calls use this key; Claude calls use the server-side Claude key.
          </p>
          <div className={`mb-3 text-[10px] font-mono uppercase tracking-widest ${
            q.data?.has_claude_key ? "text-emerald-400" : "text-amber-400"
          }`}>
            Claude API: {q.data?.has_claude_key ? "connected" : "not configured"}
          </div>
          <a
            href="https://aistudio.google.com/apikey"
            target="_blank"
            rel="noreferrer"
            data-testid="get-gemini-key-link"
            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline mb-4"
          >
            get a key at aistudio.google.com <ExternalLink className="w-3 h-3"/>
          </a>
          {hasKey ? (
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 mb-3 flex items-center justify-between gap-2" data-testid="byo-key-connected">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-0.5">connected</div>
                <div className="font-mono text-[12px] text-[var(--text-secondary)]">key: {q.data.gemini_key_hint}</div>
              </div>
              <button
                onClick={() => save.mutate({custom_gemini_key:""})}
                disabled={save.isPending}
                data-testid="remove-gemini-key-btn"
                className="p-2 border border-white/10 hover:border-[var(--accent)] text-[var(--text-muted)] hover:text-[var(--accent)]"
                title="Remove key"
              >
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          ) : (
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-3" data-testid="byo-key-empty">
               // no key connected — configure GEMINI_API_KEY or add one here
            </div>
          )}
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">
            Paste Gemini API key
          </label>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="AQ… / AIzaSy… / paste any Gemini key"
            data-testid="gemini-key-input"
            className={INPUT_CLS + " mb-3"}
          />
          <button
            onClick={() => save.mutate({custom_gemini_key: key})}
            disabled={save.isPending || !key.trim()}
            data-testid="save-gemini-key-btn"
            className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-medium py-2 flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {save.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            {hasKey ? "REPLACE KEY" : "CONNECT KEY"}
          </button>
          <div className="text-[10px] font-mono text-[var(--text-muted)] mt-2 leading-relaxed">
            We ping Google with a "reply OK" test before saving. Key is stored server-side and never sent to the client.
          </div>
        </div>

        {/* MODEL ROUTING HINT */}
        <div className="lg:col-span-2 border border-blue-500/25 bg-blue-500/5 p-4 flex gap-3 items-start" data-testid="routing-hint">
          <Rocket className="w-4 h-4 text-blue-400 shrink-0 mt-0.5"/>
          <div className="text-[12px] text-blue-200 leading-relaxed">
             <b>How routing works:</b> every orchestration, topic-generation, persona, analysis, and verification call
             uses the enabled Gemini and Claude models. Choose which models are active under <b>Webhook &amp; API</b>.
             The server-level <span className="font-mono">GEMINI_API_KEY</span> is used by default; a key saved here overrides it.
          </div>
        </div>
      </div>
    </Layout>
  );
}
